import React from 'react';
import Step from './Step';

interface StepperProps {
  steps: any[];
  currentStep: number;
}
const Stepper = ({ steps, currentStep }: StepperProps) => {
  const isSelected = (index: number) => {
    return index === currentStep || isBeforeSelected(index);
  };
  const isBeforeSelected = (index: number) => {
    return index < currentStep;
  };

  // Circle size: 5.6rem (h-[5.6rem] w-[5.6rem])
  // With justify-between, circles are positioned at edges and evenly spaced
  // We need to position lines between circles, not overlapping them

  // Calculate progress percentage
  // currentStep is 0-indexed, so we divide by (steps.length - 1) to get percentage
  // Clamp to ensure it's between 0 and 100
  const progressPercent = Math.min(
    100,
    Math.max(0, (currentStep / (steps.length - 1)) * 100)
  );

  // Circle is 5.6rem wide with 0.8rem margin on each side (mx-[0.8rem])
  // With justify-between, first circle starts at 0.8rem, last circle ends at 100% - 0.8rem
  // Line should start at right edge of first circle: 0.8rem (margin) + 5.6rem (circle) = 6.4rem
  // Line should end at left edge of last circle: 100% - 0.8rem (margin) - 5.6rem (circle) = calc(100% - 6.4rem)
  const circleRadius = 2.8; // 5.6rem / 2
  const circleMargin = 0.8; // mx-[0.8rem]
  const lineStart = circleMargin + 5.6; // 6.4rem
  const lineEndOffset = circleMargin + 5.6; // 6.4rem

  return (
    <div className="flex w-full flex-col gap-[1.6rem]">
      <div id="stepper" className="relative flex justify-between">
        {/* Continuous progress bar background - spans from first to last circle */}
        <div
          className="absolute mt-[2.6rem] hidden h-2 rounded-full bg-brand-lighter-gray sm:block dark:bg-gray-700"
          style={{
            left: `${lineStart}rem`,
            right: `${lineEndOffset}rem`,
          }}
        >
          {/* Continuous progress bar fill */}
          <div
            className="h-2 rounded-full bg-brand-secondary transition-all duration-300"
            style={{
              width: `${progressPercent}%`,
            }}
          ></div>
        </div>
        {/* Steps with circles on top (z-index ensures they appear above the progress bar) */}
        {steps.map((step, index) => (
          <Step key={step.id} {...step} selected={isSelected(index)} />
        ))}
      </div>
      <div className="hidden flex-col items-center md:hidden">
        <span className="text-[1.5rem] leading-[2.2rem] text-brand-gray">
          Status
        </span>
        <span className="font-semibold leading-[2.7rem] text-brand-light-black">
          {steps[currentStep]?.label}
        </span>
      </div>
    </div>
  );
};

export default Stepper;
